import PropTypes from 'prop-types';
import classNames from 'classnames';
import { alpha, makeStyles } from '@material-ui/core';
import ActionButton from '../../../actionButton';
import { ACTION_BUTTON_TYPES } from '../../../actionButton/config';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    departmentRoot: {
      cursor: 'default',
      display: 'grid',
      paddingTop: spacing(2),
      backgroundColor: primary.white,
      alignItems: 'center',
      justifyContent: 'space-between',
      gridTemplateColumns:
        'minmax(30px, 30px) minmax(350px, 1141px) minmax(90px, 190px) minmax(30px, 40px)',
      boxSizing: 'border-box',
      borderBottom: `1px solid ${primary.bluish7}`,
      [breakpoints.up('xLg')]: {
        gridTemplateColumns:
          'minmax(30px, 30px) minmax(420px, 1894px) minmax(160px, 170px) minmax(60px, 60px)',
      },
      '& > div:first-child': {
        opacity: 0,
        transition: 'opacity .3s ease',
        paddingTop: 0,
        height: '100%',
        alignItems: 'start',
        top: '-1px',
        position: 'relative',
      },
      '&:hover': {
        boxShadow: `0px 2px 8px 0px ${alpha(primary.black, 0.17)}`,
        transition: 'box-shadow .3s ease',
        borderRadius: 8,
        position: 'relative',
        cursor: ({ isVisible }) => {
          return isVisible ? 'pointer' : 'grabbing';
        },
        '& > div:first-child': {
          opacity: ({ isVisible }) => {
            return isVisible ? 1 : 0;
          },
          transition: 'opacity .3s ease',
          cursor: ({ isVisible }) => {
            return isVisible ? 'pointer' : 'grabbing';
          },
        },
      },
      '&:active': {
        boxShadow: `0px 2px 8px 0px ${alpha(primary.black, 0.17)}`,
        transition: 'box-shadow .3s ease',
        borderRadius: 8,
        zIndex: 999,
        position: 'relative',
        '& > div:first-child': {
          opacity: 1,
          transition: 'opacity .3s ease',
        },
      },
    },
    readOnlyRoot: {
      width: 1081,
      gridTemplateColumns:
        'minmax(0px, 0px) minmax(384px, 1051px) minmax(238px, 348px)',
    },
    headerDisabled: {
      gridTemplateColumns:
        'minmax(0px, 0px) minmax(532px, 1896px) minmax(160px, 190px) minmax(30px, 40px)',
      [breakpoints.up('xLg')]: {
        gridTemplateColumns:
          'minmax(0px, 0px) minmax(532px, 1941px) minmax(160px, 170px) minmax(30px, 40px)',
      },
    },
    item: {
      boxSizing: 'border-box',
      padding: spacing(4, 2),
      [breakpoints.up('xLg')]: {
        padding: spacing(4),
      },
    },
    description: {
      minWidth: 0,
      height: '100%',
      minHeight: 70,
      padding: spacing(0, 4, 4, 0.5),
    },
    readOnlyDescription: {
      padding: spacing(0, 4, 4, 8),
    },
    dragItemWrapper: {
      display: 'flex',
      alignItems: 'center',
      minWidth: 0,
      padding: spacing(4, 4, 4, 0),
      [breakpoints.up('xLg')]: {
        padding: spacing(4, 4, 4, 0),
      },
    },
    readOnlyDragItem: {
      padding: spacing(0),
    },
    actions: {
      boxSizing: 'border-box',
      cursor: 'default',
      height: '100%',
      padding: spacing(0, 2),
      [breakpoints.up('xLg')]: {
        padding: spacing(0, 2),
      },
    },
    people: {
      width: 'fit-content',
      height: '100%',
    },
    dragButton: {
      position: 'relative',
      width: 22,
      height: 22,
      flexShrink: 0,
      marginLeft: spacing(2),
      marginRight: spacing(1.5),
      zIndex: 11,
      overflow: 'visible',
      '& button': {
        width: 22,
        height: 22,
      },
    },
    dragIcon: {
      width: 8,
      height: 14,
    },
    dragTooltipLabel: {
      fontFamily: 'ProximaNova-Regular',
    },
  })
);

const Department = ({
  translations,
  department,
  dragRef = () => {},
  dragProps = null,
  canEdit,
  canDelete,
  isVisible = false,
}) => {
  const classes = useStyles({ isVisible });
  const { description, people, actions } = department;

  return (
    <div
      ref={dragRef}
      className={classNames(classes.departmentRoot, {
        [classes.headerDisabled]: !canEdit,
      })}
      {...(dragProps ? { ...dragProps.draggableProps } : {})}
    >
      <div
        className={classNames(classes.item, classes.dragItemWrapper, {
          [classes.readOnlyDragItem]: !canEdit,
        })}
      >
        {canEdit && (
          <ActionButton
            className={classes.dragButton}
            iconClass={classes.dragIcon}
            tooltipLabelClass={classes.dragTooltipLabel}
            type={ACTION_BUTTON_TYPES.DRAG}
            tooltipText={translations.departmentDragTooltip}
            {...(dragProps ? { ...dragProps.dragHandleProps } : {})}
          />
        )}
      </div>
      <div
        className={classNames(classes.item, classes.description, {
          [classes.readOnlyDescription]: !canEdit,
        })}
      >
        {description}
      </div>
      <div className={classes.people}>{people}</div>
      {(canEdit || canDelete) && (
        <div className={classes.actions}>{actions}</div>
      )}
    </div>
  );
};

Department.propTypes = {
  translations: PropTypes.object.isRequired,
  isVisible: PropTypes.bool,
  department: PropTypes.object.isRequired,
  canEdit: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool.isRequired,
  dragProps: PropTypes.object,
  dragRef: PropTypes.func,
};

export default Department;
